import React, { useCallback, useMemo } from 'react';
import { Button, Progress } from 'antd';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useAuth } from '@hooks/useAuth';
import { onboardingPercentage } from '@helpers/onboardingSteps';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';
import type { OnboardingStepsType } from '@context/OnboardingProcessProvider';
import { redirectToSubdomain } from '@components/OrganizationBoundary';
import {
  DEMO_SUBDOMAIN,
  DEMO_TOUR_MEDIA_VIEWER_ASSET_ID,
  DEMO_TOUR_WORKSPACE_ID
} from '@config/constants';

import './TabInteractiveTour.less';

type TabInteractiveTourProps = {
  onCloseModal: () => void;
};

export default function TabInteractiveTour({
  onCloseModal
}: TabInteractiveTourProps) {
  const navigate = useNavigate();
  const isDemoOrganization = useIsDemoOrganization();
  const { user } = useAuth(true, 'internal');
  const [currentWorkspace] = useCurrentWorkspace(true);

  const { completed, total, steps } = useMemo(() => {
    const isContributor = currentWorkspace.role === 'workspace://contributor';
    const { completed, total, steps } = onboardingPercentage(
      isContributor,
      user.completedOnboardingSteps
    );
    return { completed, total, steps };
  }, [user.completedOnboardingSteps, currentWorkspace.role]);

  const onOpenTour = useCallback(
    (path: string, startTour: OnboardingStepsType) => {
      if (isDemoOrganization) {
        onCloseModal();
        navigate(`/${path}?startTour=${startTour}`);
        return;
      }
      redirectToSubdomain(DEMO_SUBDOMAIN, path, [
        { name: 'startTour', value: startTour }
      ]);
    },
    [onCloseModal, navigate, isDemoOrganization]
  );

  const onStartTour = (type: OnboardingStepsType) => {
    switch (type) {
      case 'create-campaign':
        onOpenTour(
          `campaigns/all/${DEMO_TOUR_WORKSPACE_ID}`,
          'create-campaign'
        );
        break;
      case 'upload-asset':
        onOpenTour(`campaigns/all/${DEMO_TOUR_WORKSPACE_ID}`, 'upload-asset');
        break;
      case 'route-for-approval':
        onOpenTour(
          `campaigns/all/${DEMO_TOUR_WORKSPACE_ID}`,
          'route-for-approval'
        );
        break;
      case 'review-asset':
        onOpenTour(
          `media-viewer/${DEMO_TOUR_MEDIA_VIEWER_ASSET_ID}/${DEMO_TOUR_MEDIA_VIEWER_ASSET_ID}`,
          'review-asset'
        );
        break;
      case 'create-task-from-comment':
        onOpenTour(
          `media-viewer/${DEMO_TOUR_MEDIA_VIEWER_ASSET_ID}/${DEMO_TOUR_MEDIA_VIEWER_ASSET_ID}`,
          'create-task-from-comment'
        );
        break;
      default:
        onCloseModal();
    }
  };

  return (
    <div className="bootcamp-tab-interactive-tour">
      <div className="bootcamp-modal_tabs-label">Streamwork Bootcamp</div>
      <div className="bootcamp-modal_tabs-title">Interactive Tour</div>
      <div className="bootcamp-modal_tabs-text">
        Browse our list of quick guided tours to see StreamWork in action.
      </div>
      <div className="bootcamp-modal_tabs-progress">
        {Array.from({ length: total }).map((_, index) => (
          <Progress
            key={index}
            percent={index < completed ? 100 : 0}
            showInfo={false}
            strokeColor="#3553DB"
            trailColor="#F5F6FD"
          />
        ))}
      </div>
      <div className="bootcamp-tab-interactive-tour_content">
        {steps.map((item) => (
          <div
            key={item.type}
            className={classNames('bootcamp-tab-interactive-tour_block', {
              'b-done': user.completedOnboardingSteps.includes(item.type)
            })}
          >
            <div className="bootcamp-tab-interactive-tour_block-checkmark" />
            <div className="bootcamp-tab-interactive-tour_block-icon">
              {item.icon}
            </div>
            <div className="bootcamp-tab-interactive-tour_block-info">
              <div className="bootcamp-tab-interactive-tour_block-title">
                {item.title}
              </div>
              <div className="bootcamp-tab-interactive-tour_block-text">
                {item.description}
              </div>
            </div>
            <div className="bootcamp-tab-interactive-tour_block-button">
              <Button
                className="start-btn"
                type="primary"
                onClick={() => onStartTour(item.type)}
              >
                {item.button}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
